// Client layer (TS-2.5.03) - the archive-browsing view itself (BRD section 8.2: "see the deck as
// it stood"). Composes useVersionRoadmap with the SAME RoadmapBoard/StatCards/Legend components
// the live roadmap uses (a version's RoadmapView is the identical shape, frozen - decisions.md
// D-003), wrapped in an unmistakable "as of {reportDate}" banner so nobody mistakes a frozen board
// for the live one. Read-only: no filter bar, no edit affordances.
import { useMemo } from "react";
import { RoadmapBoard } from "../roadmap/RoadmapBoard";
import { StatCards } from "../roadmap/StatCards";
import { Legend } from "../roadmap/Legend";
import { buildLegend } from "../roadmap/lib/legend";
import type { ProjectView } from "../../api/generated/contracts";
import { useVersionRoadmap } from "./hooks/useVersionRoadmap";
import { formatDate } from "./lib/deckFormat";
import { strings } from "../../strings";

export interface FrozenRoadmapPanelProps {
  readonly versionKey: string;
  readonly onBack: () => void;
}

const S = strings.decks.snapshot;

function projectsByKey(rows: ReadonlyArray<{ rows: readonly ProjectView[] }>): ReadonlyMap<string, ProjectView> {
  const map = new Map<string, ProjectView>();
  for (const group of rows) {
    for (const project of group.rows) map.set(project.key, project);
  }
  return map;
}

export function FrozenRoadmapPanel({ versionKey, onBack }: FrozenRoadmapPanelProps) {
  const { view, loading, error } = useVersionRoadmap(versionKey);
  const byKey = useMemo(() => (view ? projectsByKey(view.cards) : new Map()), [view]);

  return (
    <section className="eg-deck-snapshot" aria-labelledby="eg-deck-snapshot-heading">
      <button type="button" onClick={onBack}>
        {S.back}
      </button>

      {loading && !view && (
        <div role="status" aria-live="polite">
          {S.loading}
        </div>
      )}

      {error && (
        <div role="alert">
          <p>{S.errorTitle}</p>
          <p>{error.message}</p>
        </div>
      )}

      {view && (
        <>
          <div role="note" className="eg-deck-snapshot__banner" id="eg-deck-snapshot-heading">
            {S.bannerPrefix} {formatDate(view.header.reportDate)} ({view.header.title}). {S.bannerSuffix}
          </div>

          <StatCards cards={view.counts.cards} projectsByKey={byKey} />
          <RoadmapBoard
            board={view.board}
            statuses={[]}
            selectedStatuses={[]}
            dimmedOpacity={view.filterSemantics.dimmedOpacity}
            emptyCellPlaceholder={view.profile.emptyCellPlaceholder}
            projectsByKey={byKey}
          />
          <Legend entries={buildLegend(view.board, [])} />
        </>
      )}
    </section>
  );
}
